import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import type { ListPendingQuizzesResponse, SavePendingQuizRequest } from '@/types';

const PENDING_QUIZZES_KEY = ['pendingQuizzes'];
const POLL_FAST = 30 * 1000;     // 30 seconds
const POLL_SLOW = 5 * 60 * 1000; // 5 minutes
/**
 * Hook to fetch and manage pending quizzes
 * Automatically polls for updates every 30 seconds when items are processing
 */
export function usePendingQuizzes() {
  const { user } = useAuth();

  const query = useQuery<ListPendingQuizzesResponse>({
    queryKey: PENDING_QUIZZES_KEY,
    queryFn: () => api.listPendingQuizzes(),
    enabled: !!user,

    // Logic: Poll fast if processing > 0, otherwise disable polling
    refetchInterval: (query) => {
      const data = query.state.data;
      // 1. If no data exists yet, don't refetch
      if (!data) return POLL_SLOW;

      // 2. Check the processing count safely
      const processingCount = data.counts?.processing ?? 0;

      // 3. If there are items processing, poll every 30 seconds
      if (processingCount > 0) {
        return POLL_FAST;
      }

      // 4. Otherwise (0 processing or empty), don't refetch
      return false;
    },

    refetchIntervalInBackground: false,
    staleTime: 10000,
  });
  return {
    pendingQuizzes: query.data?.pendingQuizzes ?? [],
    counts: query.data?.counts ?? { total: 0, ready: 0, processing: 0, failed: 0 },
    readyCount: query.data?.counts?.ready ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to save a pending quiz to quizlets
 */
export function useSavePendingQuiz() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: SavePendingQuizRequest }) =>
      api.savePendingQuiz(id, data),
    onSuccess: () => {
      // Invalidate pending quizzes to refresh the list
      queryClient.invalidateQueries({ queryKey: PENDING_QUIZZES_KEY });
      // Also invalidate quizlets since a new one was created
      queryClient.invalidateQueries({ queryKey: ['quizlets'] });
    },
  });
}

/**
 * Hook to discard/delete a pending quiz
 */
export function useDeletePendingQuiz() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deletePendingQuiz(id),
    onSuccess: () => {
      // Invalidate pending quizzes to refresh the list
      queryClient.invalidateQueries({ queryKey: PENDING_QUIZZES_KEY });
    },
  });
}

/**
 * Hook to manually trigger a refetch of pending quizzes
 */
export function useRefreshPendingQuizzes() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: PENDING_QUIZZES_KEY });
  };
}
